import { config } from '@ps/config/client';
import { auth } from '@ps/functions/firebase/auth';
import type { popstore } from '@ps/types';

import type { MediaCategory, MediaObjectTag } from '../types/MediaCategory';

/**
 * Per-category metadata accepted by the media-gallery `/upload`
 * endpoint. Mirrors PopshopRN's `MediaGalleryPreSignedUrlRequest`
 * discriminated union but flattened into a single optional bag — the
 * server tolerates extra keys, so we keep the call sites simple.
 */
export interface RequestPresignedUrlMetadata {
  /** Original filename. The server falls back to a nanoid when omitted. */
  filename?: string;
  /** Browser MIME type. Stored on `metaData.mediatype`. */
  contentType?: string;
  /** File size in bytes. */
  size?: number;
  /** Human-readable caption / title for the asset. */
  caption?: string;
  /** Free-form `alt` text; defaults to `caption` server-side. */
  alt?: string;
  /** Stable domain tag — see `MediaObjectTag`. */
  tag?: MediaObjectTag;
  /** Author user id; recorded on the gallery row for ownership lookup. */
  createdBy?: string;
  /** Owning post ID — only applies to `popStorePosts`. */
  internalPostId?: string;
  /** Owning message ID — only applies to `popStoreGroupMessages`. */
  internalMessageId?: string;
  /** Client-extracted dims / blurhash / brightness. */
  imageMetadata?: popstore.MediaFileMetadata;
}

export interface RequestPresignedUrlOptions {
  category: MediaCategory;
  metadata: RequestPresignedUrlMetadata;
  signal?: AbortSignal;
}

export interface PresignedUploadTarget {
  /** Pre-signed PUT url for the S3 upload (single-PUT flow). */
  uploadUrl: string;
  /** CDN URL where the asset will be reachable post-upload. */
  cdnUrl: string;
  /** Media-gallery row id (used to patch metadata later). */
  objectId: string;
  /** Owner user id baked into the gallery row. */
  userId: string;
  /**
   * Marks the gallery row as `status: 'uploaded'` once the PUT
   * succeeded. Fire-and-forget: callers shouldn't await this if
   * they want to surface the CDN URL immediately. The server's
   * background worker re-flips the flag if the call drops.
   */
  markComplete: () => Promise<void>;
}

interface PresignedUrlResponseBody {
  uploadFileUrl: string;
  image?: GalleryItemPayload;
  clip?: GalleryItemPayload;
}

interface GalleryItemPayload {
  id: string;
  cdnUrl: string;
  metaIdentifier: string;
  metaData: {
    imageURL?: string;
    videoURL?: string;
  };
}

/**
 * Request a pre-signed S3 PUT URL from the media-gallery service.
 *
 * Mirrors PopshopRN's `useMediaGalleryPreSignedUrl()` factory — the
 * shape is `{ uploadUrl, cdnUrl, objectId, userId, markComplete }`
 * instead of the legacy `[url, item, fn]` tuple because the named-
 * field record is easier to spread into call sites.
 *
 * Standalone (not a hook) so it composes inside service hooks AND
 * non-React contexts. The Firebase ID-token lookup happens here so
 * callers don't have to thread auth through.
 */
export async function requestPresignedUrl(
  options: RequestPresignedUrlOptions,
): Promise<PresignedUploadTarget> {
  const { category, metadata, signal } = options;

  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

  const token = await auth().currentUser?.getIdToken();
  if (!token) throw new Error('User is not authenticated');

  const filename = metadata.filename ?? generateFallbackFilename();
  const caption = metadata.caption ?? stripExtension(filename);

  const payload = buildRequestPayload({
    category,
    filename,
    caption,
    metadata,
  });

  const response = await fetch(`${config.awsMediaGalleryBaseUrl}/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(payload),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Media gallery upload request failed: ${response.status}`);
  }

  const body = (await response.json()) as PresignedUrlResponseBody;
  const item = body.image ?? body.clip;
  if (!item) {
    throw new Error('Media gallery response missing image/clip payload');
  }

  const assetPath = item.metaData.imageURL ?? item.metaData.videoURL;
  if (!assetPath) {
    throw new Error('Media gallery response missing asset path');
  }

  const userId = item.id;
  const objectId = item.metaIdentifier;

  return {
    uploadUrl: body.uploadFileUrl,
    cdnUrl: `${item.cdnUrl}${assetPath}`,
    objectId,
    userId,
    markComplete: () => markUploadComplete({ token, userId, objectId }),
  };
}

function generateFallbackFilename(): string {
  // Cheap pseudo-id — only used when the caller doesn't pass a filename.
  // Not a security boundary; the server assigns the canonical id.
  const random = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}-${random}.jpg`;
}

function stripExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  return lastDot > 0 ? filename.slice(0, lastDot) : filename;
}

function buildRequestPayload({
  category,
  filename,
  caption,
  metadata,
}: {
  category: MediaCategory;
  filename: string;
  caption: string;
  metadata: RequestPresignedUrlMetadata;
}): Record<string, unknown> {
  const base = {
    fileName: filename,
    fileMeta: { fileCategory: category },
  };

  const commonMetaData = {
    title: caption,
    caption,
    mediatype: metadata.contentType,
    size: metadata.size,
    alt: metadata.alt ?? caption,
    createdAt: new Date().toISOString(),
    createdBy: metadata.createdBy,
    tag: metadata.tag,
    dimensions:
      metadata.imageMetadata?.width != null &&
      metadata.imageMetadata.height != null
        ? {
            width: metadata.imageMetadata.width,
            height: metadata.imageMetadata.height,
          }
        : undefined,
    blurhash: metadata.imageMetadata?.blurhash,
  };

  // Per-category extras mirror PopshopRN's branch in
  // `useMediaGalleryPreSignedUrl.ts`. Each category carries its own
  // server-side persistence behavior, so we attach internalPostId /
  // internalMessageId only to the categories that consume them.
  if (category === 'popStorePosts') {
    return {
      ...base,
      metaData: {
        ...commonMetaData,
        internalPostId: metadata.internalPostId,
      },
    };
  }

  if (category === 'popStoreGroupMessages') {
    return {
      ...base,
      metaData: {
        ...commonMetaData,
        internalMessageId: metadata.internalMessageId,
        filename,
      },
    };
  }

  return { ...base, metaData: commonMetaData };
}

async function markUploadComplete({
  token,
  userId,
  objectId,
}: {
  token: string;
  userId: string;
  objectId: string;
}): Promise<void> {
  const response = await fetch(
    `${config.awsMediaGalleryBaseUrl}/updateMediaMetadata`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token,
      },
      body: JSON.stringify({
        id: userId,
        metaIdentifier: objectId,
        metaData: {
          status: 'uploaded',
          updatedAt: new Date().toISOString(),
        },
      }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to mark media as uploaded: ${response.status} ${response.statusText}`,
    );
  }
}
